import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { getApiOrigin } from "../components/front/env.js";
import { tLang } from "../i18n.js";
import LangSelect from "../components/front/LangSelect.jsx";
import Price from "../components/Price.jsx";
import SubdomainForm from "../components/SubdomainForm.jsx";
import CallbackWidget from "../components/CallbackWidget.jsx";
import SeoContactSection from "../components/seo/SeoContactSection.jsx";
import CountryBanner from "../components/CountryBanner.jsx";

import tgIcon from "../img/icons/tg.png";
import waIcon from "../img/icons/wa.png";
import instIcon from "../img/icons/inst.png";

import PhoneInput, {
  getCountryCallingCode,
  isPossiblePhoneNumber,
  isValidPhoneNumber,
} from "react-phone-number-input";
import "react-phone-number-input/style.css";

const API_ORIGIN = getApiOrigin();

const ENDPOINTS = {
  resetRequest: () => `${API_ORIGIN}/reset-password/request`,
  resetConfirm: () => `${API_ORIGIN}/reset-password/confirm`,
  login: () => `${API_ORIGIN}/login`,
  register: () => `${API_ORIGIN}/register`,
  signupComplete: () => `${API_ORIGIN}/signup-complete`,
  registerSubdomain: () => `${API_ORIGIN}/register-subdomain`,
};

const LANDING_LANG_STORAGE_KEY = "landingLang";
const SITE_ORIGIN = "https://best-place.club";

const DEFAULT_SUBSCRIPTION_PRICING = Object.freeze({
  market: "eu",
  currency: "EUR",
  basicMonthlyMinor: 6900,
});

function normalizeSubscriptionPricing(value) {
  const market =
    String(value?.market || "")
      .trim()
      .toLowerCase() === "ua"
      ? "ua"
      : "eu";

  const fallback =
    market === "ua"
      ? {
          market: "ua",
          currency: "UAH",
          basicMonthlyMinor: 99000,
        }
      : DEFAULT_SUBSCRIPTION_PRICING;

  const rawAmount = Number(value?.basicMonthlyMinor);

  return {
    market,
    currency: market === "ua" ? "UAH" : "EUR",

    basicMonthlyMinor:
      Number.isInteger(rawAmount) && rawAmount >= 0
        ? rawAmount
        : fallback.basicMonthlyMinor,
  };
}

const LANDING_FOOTER_MAIN_LINKS = [
  {
    key: "video",
    label: {
      de: "Video",
      en: "Video",
      uk: "Відео",
      fr: "Vidéo",
      nl: "Video",
      pl: "Wideo",
      it: "Video",
      da: "Video",
      es: "Vídeo",
      no: "Video",
    },
    getPath: (lang) =>
    lang === "en"
      ? "/en/demo"
      : lang === "no"
      ? "/no/demo"
      : "/de/demo",
    },
  {
    key: "pricing",
    label: {
      de: "Preise",
      en: "Pricing",
      uk: "Ціни",
      fr: "Tarifs",
      nl: "Prijzen",
      pl: "Cennik",
      it: "Prezzi",
      da: "Priser",
      es: "Precios",
      no: "Priser",
    },
    slug: "pricing",
  },
  {
    key: "contact",
    label: {
      de: "Impressum",
      en: "Contact",
      uk: "Контакти",
      fr: "Contact",
      nl: "Contact",
      pl: "Kontakt",
      it: "Contatto",
      da: "Kontakt",
      es: "Contacto",
      no: "Kontakt",
    },
    slug: "impressum",
  },
];

const LANDING_FOOTER_LEGAL_LINKS = [
  {
    key: "terms",
    label: {
      de: "AGB",
      en: "Terms",
      uk: "Умови",
      fr: "Conditions",
      nl: "Voorwaarden",
      pl: "Regulamin",
      it: "Termini",
      da: "Vilkår",
      es: "Términos",
      no: "Vilkår",
    },
    slug: "terms",
  },
  {
    key: "privacy",
    label: {
      de: "Datenschutz",
      en: "Privacy",
      uk: "Конфіденційність",
      fr: "Confidentialité",
      nl: "Privacy",
      pl: "Prywatność",
      it: "Privacy",
      da: "Privatliv",
      es: "Privacidad",
      no: "Personvern",
    },
    slug: "privacy",
  },
  {
    key: "refund",
    label: {
      de: "Stornierung & Rückerstattung",
      en: "Cancellation & Refund",
      uk: "Скасування та повернення",
      fr: "Annulation et remboursement",
      nl: "Annulering en terugbetaling",
      pl: "Anulowanie i zwrot",
      it: "Cancellazione e rimborso",
      da: "Annullering og refundering",
      es: "Cancelación y reembolso",
      no: "Kansellering og refusjon",
    },
    slug: "refund",
  },
];

function getFooterLinkText(labels, lang) {
  return labels?.[lang] || labels?.en || labels?.de || "";
}


const SEO_BY_LANG = {
  de: {
    htmlLang: "de",
    path: "/de/",
    title: "Digitale Lösungen für Restaurants | Best Place",
    description:
      "Digitale Speisekarte mit QR-Code, Online-Tischreservierung, Bestellungen und Administrationsbereich für Restaurants.",
  },
  en: {
    htmlLang: "en",
    path: "/en/",
    title: "Digital solutions for restaurants | Best Place",
    description:
      "Digital menu with QR code, online table reservations, orders and admin tools for restaurants.",
  },
  uk: {
    htmlLang: "uk",
    path: "/uk/",
    title: "Цифрові рішення для ресторанів | Best Place",
    description:
      "Цифрове меню з QR-кодом, онлайн-бронювання столів, замовлення та адмін-панель для ресторанів.",
  },
  fr: {
    htmlLang: "fr",
    path: "/fr/",
    title: "Solutions numériques pour restaurants | Best Place",
    description:
      "Menu numérique avec QR code, réservation de tables en ligne, commandes et espace d’administration pour restaurants.",
  },
  nl: {
    htmlLang: "nl",
    path: "/nl/",
    title: "Digitale oplossingen voor restaurants | Best Place",
    description:
      "Digitaal menu met QR-code, online tafelreserveringen, bestellingen en beheertools voor restaurants.",
  },
  pl: {
    htmlLang: "pl",
    path: "/pl/",
    title: "Cyfrowe rozwiązania dla restauracji | Best Place",
    description:
      "Cyfrowe menu z kodem QR, rezerwacje stolików online, zamówienia i panel administracyjny dla restauracji.",
  },
  it: {
    htmlLang: "it",
    path: "/it/",
    title: "Soluzioni digitali per ristoranti | Best Place",
    description:
      "Menu digitale con codice QR, prenotazione tavoli online, ordini e pannello di amministrazione per ristoranti.",
  },
  da: {
    htmlLang: "da",
    path: "/da/",
    title: "Digitale løsninger til restauranter | Best Place",
    description:
      "Digital menu med QR-kode, online bordreservationer, bestillinger og administrationsværktøjer til restauranter.",
  },
  es: {
    htmlLang: "es",
    path: "/es/",
    title: "Soluciones digitales para restaurantes | Best Place",
    description:
      "Menú digital con código QR, reservas de mesas online, pedidos y panel de administración para restaurantes.",
  },
  no: {
    htmlLang: "no",
    path: "/no/",
    title: "Digitale løsninger for restauranter | Best Place",
    description:
      "Digital meny med QR-kode, online bordreservasjoner, bestillinger og administrasjonsverktøy for restauranter.",
  },
};

const AVAILABLE_LANGS = [
  "de",
  "en",
  "uk",
  "fr",
  "nl",
  "pl",
  "it",
  "da",
  "es",
  "no",
];

const LANDING_PATH_BY_LANG = {
  de: "/de/",
  en: "/en/",
  uk: "/uk/",
  fr: "/fr/",
  nl: "/nl/",
  pl: "/pl/",
  it: "/it/",
  da: "/da/",
  es: "/es/",
  no: "/no/",
};

function normalizeLandingLang(value) {
  const v = String(value || "").trim().toLowerCase();
  return AVAILABLE_LANGS.includes(v) ? v : "de";
}

function safeReadLandingLang() {
  try {
    const v = window?.localStorage?.getItem(LANDING_LANG_STORAGE_KEY);
    return AVAILABLE_LANGS.includes(v) ? v : null;
  } catch {
    return null;
  }
}

function upsertMetaByName(name, content) {
  if (typeof document === "undefined") return;

  let el = document.head.querySelector(`meta[name="${name}"]`);

  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", name);
    document.head.appendChild(el);
  }

  el.setAttribute("content", content);
}

function upsertLink(selector, attrs) {
  if (typeof document === "undefined") return;

  let el = document.head.querySelector(selector);

  if (!el) {
    el = document.createElement("link");
    document.head.appendChild(el);
  }

  Object.entries(attrs).forEach(([key, value]) => {
    el.setAttribute(key, value);
  });
}

function removeOldHreflangLinks() {
  if (typeof document === "undefined") return;

  document.head
    .querySelectorAll('link[data-landing-hreflang="true"]')
    .forEach((el) => el.remove());
}

function addHreflangLink(hreflang, href) {
  if (typeof document === "undefined") return;

  const el = document.createElement("link");
  el.setAttribute("rel", "alternate");
  el.setAttribute("hreflang", hreflang);
  el.setAttribute("href", href);
  el.setAttribute("data-landing-hreflang", "true");
  document.head.appendChild(el);
}


function useLandingSeo(lang) {
  useEffect(() => {
    const L = normalizeLandingLang(lang);

    // Прибираємо теги, створені попереднім
    // запуском цього хуку
    removeOldHreflangLinks();

    // Для DE, EN, NO метаданими керує SeoRouteHead
    if (["de", "en", "no"].includes(L)) {
      return;
    }

    const seo = SEO_BY_LANG[L] || SEO_BY_LANG.de;


    document.documentElement.setAttribute("lang", seo.htmlLang);
    document.title = seo.title;

    upsertMetaByName("description", seo.description);

    upsertLink('link[rel="canonical"]', {
      rel: "canonical",
      href: `${SITE_ORIGIN}${seo.path}`,
    });

    document.head
    .querySelectorAll('link[rel="alternate"][hreflang]')
    .forEach((item) => item.remove());

    addHreflangLink("de", `${SITE_ORIGIN}/de/`);
    addHreflangLink("en", `${SITE_ORIGIN}/en/`);
    addHreflangLink("uk", `${SITE_ORIGIN}/uk/`);
    addHreflangLink("fr", `${SITE_ORIGIN}/fr/`);
    addHreflangLink("nl", `${SITE_ORIGIN}/nl/`);
    addHreflangLink("pl", `${SITE_ORIGIN}/pl/`);
    addHreflangLink("it", `${SITE_ORIGIN}/it/`);
    addHreflangLink("da", `${SITE_ORIGIN}/da/`);
    addHreflangLink("es", `${SITE_ORIGIN}/es/`);
    addHreflangLink("no", `${SITE_ORIGIN}/no/`);
    addHreflangLink("x-default", `${SITE_ORIGIN}/de/`);
  }, [lang]);
}

function countLocalDigitsFromInputValue(rawValue, country) {
  const digits = stripToDigits(rawValue);
  const cc = getCountryCallingCodeSafe(country);

  let local = cc && digits.startsWith(cc) ? digits.slice(cc.length) : digits;

  if (TRUNK_PREFIX_COUNTRIES.has(country)) {
    local = local.replace(/^0+/, "");
  }

  return local.length;
}

async function notifyTelegramAboutRegister(phone, lang = "de") {
  try {
    await fetch(`${API_ORIGIN}/tg/send-register`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        phone,
        lang,
        page: typeof window !== "undefined" ? window.location.href : "",
        sentAt: new Date().toISOString(),
        source: "landing-register",
      }),
    });
  } catch (e) {
    console.error("notifyTelegramAboutRegister error:", e);
  }
}

async function safeJson(res) {
  try {
    return await res.json();
  } catch {
    return {};
  }
}

function setRef(ref, node) {
  if (!ref) return;
  if (typeof ref === "function") ref(node);
  else ref.current = node;
}

const PhoneField = React.forwardRef(function PhoneField(
  { inputRef, country, ...props },
  ref
) {
  const handleKeyDown = (e) => {
    props.onKeyDown?.(e);
    if (e.defaultPrevented) return;

    const key = e.key;

    const isMetaCombo =
      e.ctrlKey || e.metaKey || e.altKey;

    if (isMetaCombo) return;

    const allowedControlKeys = new Set([
      "Backspace",
      "Delete",
      "Tab",
      "Escape",
      "Enter",
      "ArrowLeft",
      "ArrowRight",
      "ArrowUp",
      "ArrowDown",
      "Home",
      "End",
    ]);

    if (allowedControlKeys.has(key)) return;

    // дозволяємо тільки цифри
    if (!/^\d$/.test(key)) {
      e.preventDefault();
      return;
    }

    const input = e.currentTarget;
    const currentValue = input.value || "";
    const selectionStart = input.selectionStart ?? currentValue.length;
    const selectionEnd = input.selectionEnd ?? currentValue.length;

    const nextValue =
      currentValue.slice(0, selectionStart) +
      key +
      currentValue.slice(selectionEnd);

    const nextLocalLen = countLocalDigitsFromInputValue(nextValue, country);
    const maxLocal = getMaxLocalDigits(country);

    if (nextLocalLen > maxLocal) {
      e.preventDefault();
    }
  };

  const handlePaste = (e) => {
    props.onPaste?.(e);
    if (e.defaultPrevented) return;

    const pasted = e.clipboardData?.getData("text") ?? "";
    if (!pasted) return;

    const input = e.currentTarget;
    const currentValue = input.value || "";
    const selectionStart = input.selectionStart ?? currentValue.length;
    const selectionEnd = input.selectionEnd ?? currentValue.length;

    const nextValue =
      currentValue.slice(0, selectionStart) +
      pasted +
      currentValue.slice(selectionEnd);

    const nextLocalLen = countLocalDigitsFromInputValue(nextValue, country);
    const maxLocal = getMaxLocalDigits(country);

    if (nextLocalLen > maxLocal) {
      e.preventDefault();
    }
  };

  return (
    <input
      {...props}
      ref={(node) => {
        setRef(ref, node);
        setRef(inputRef, node);
      }}
      onKeyDown={handleKeyDown}
      onPaste={handlePaste}
      id="auth-email"
      className="input"
      placeholder=" "
      type="tel"
      inputMode="tel"
      autoComplete="tel"
    />
  );
});

// practical upper bounds for national/local digits in international input
const MAX_LOCAL_DIGITS_BY_COUNTRY = {
  DE: 11,
  GB: 10,
  FR: 9,
  ES: 9,
  IT: 10,
  PL: 9,
  NL: 9,
  BE: 9,
  AT: 11,
  CH: 9,
  CZ: 9,
  SK: 9,
  HU: 9,
  RO: 9,
  PT: 9,
  SE: 10,
  NO: 8,
  DK: 8,
  FI: 10,
  UA: 9,
};

const FALLBACK_MAX_LOCAL = 12;

// countries where users often type local leading trunk "0"
// in international format it should be removed: +380671234567, not +3800671234567
const TRUNK_PREFIX_COUNTRIES = new Set([
  "UA",
  "DE",
  "FR",
  "IT",
  "PL",
  "RO",
  "CZ",
  "SK",
  "HU",
  "AT",
  "BE",
  "NL",
  "ES",
  "PT",
  "GB",
]);

function getCountryCallingCodeSafe(country) {
  try {
    return String(getCountryCallingCode(country));
  } catch {
    return "";
  }
}

function getMaxLocalDigits(country) {
  return MAX_LOCAL_DIGITS_BY_COUNTRY[country] ?? FALLBACK_MAX_LOCAL;
}

function stripToDigits(value) {
  return String(value || "").replace(/\D/g, "");
}

function normalizePhoneForCountry(rawValue, country) {
  if (!rawValue) return "";

  const digits = stripToDigits(rawValue);
  const cc = getCountryCallingCodeSafe(country);

  let local = cc && digits.startsWith(cc) ? digits.slice(cc.length) : digits;

  if (TRUNK_PREFIX_COUNTRIES.has(country)) {
    local = local.replace(/^0+/, "");
  }

  local = local.slice(0, getMaxLocalDigits(country));

  return cc ? `+${cc}${local}` : `+${local}`;
}

function getLocalDigitsLength(rawValue, country) {
  const normalized = normalizePhoneForCountry(rawValue, country);
  const digits = stripToDigits(normalized);
  const cc = getCountryCallingCodeSafe(country);

  if (!digits) return 0;
  if (cc && digits.startsWith(cc)) return digits.slice(cc.length).length;
  return digits.length;
}

const SUPPORTED_PHONE_COUNTRIES = new Set(
  Object.keys(MAX_LOCAL_DIGITS_BY_COUNTRY)
);

function normalizeCountryCode(value) {
  const country = String(value || "").trim().toUpperCase();
  return SUPPORTED_PHONE_COUNTRIES.has(country) ? country : "";
}

function detectCountryFromBrowser() {
  const languages = [
    ...(navigator.languages || []),
    navigator.language,
  ].filter(Boolean);

  for (const item of languages) {
    const locale = String(item || "").replace("_", "-");
    const parts = locale.split("-");
    const lang = parts[0]?.toLowerCase();
    const region = normalizeCountryCode(parts[1]);

    if (region) return region;

    if (lang === "de") return "DE";
    if (lang === "uk") return "UA";
    if (lang === "en") return "GB";
  }

  return "UA";
}

async function detectCountryFromIp() {
  try {
    const res = await fetch("https://ipapi.co/json/", {
      cache: "no-store",
    });

    if (!res.ok) return "";

    const data = await res.json();
    return normalizeCountryCode(data?.country_code);
  } catch {
    return "";
  }
}

function getAuthStepFromPath(pathname) {
  const p = String(pathname || "").toLowerCase();

  if (p.endsWith("/login")) return "login";
  if (p.endsWith("/register")) return "register";

  return null;
}


const LANDING_SEO_LINKS_COPY = {
  de: {
    title: "Digitale Restaurantlösungen von Best Place",
    text:
      "Erfahren Sie mehr über digitale Speisekarten, QR-Code-Menüs, mehrsprachige Menüs und Online-Tischreservierung für Restaurants.",
  },
  en: {
    title: "Digital restaurant tools from Best Place",
    text:
      "Learn more about digital menus, QR code menus, multilingual menus and online table reservations for restaurants.",
  },
  no: {
    title: "Digitale restaurantverktøy fra Best Place",
    text:
      "Les mer om digitale menyer, QR-kode-menyer, flerspråklige menyer og online bordreservasjoner for restauranter.",
  },
};

const LANDING_SEO_LINKS = [
  {
    key: "digitalMenu",
    labelKey: "front.landingSeo.links.digitalMenu",
    path: {
      de: "/de/digitale-speisekarte",
      en: "/en/digital-menu-for-restaurants",
      uk: "/uk/tsyfrove-menu-dlya-restoraniv",
      fr: "/fr/menu-numerique-pour-restaurants",
      nl: "/nl/digitaal-menu-voor-restaurants",
      pl: "/pl/cyfrowe-menu-dla-restauracji",
      it: "/it/menu-digitale-per-ristoranti",
      da: "/da/digital-menu-til-restauranter",
      es: "/es/menu-digital-para-restaurantes",
      no: "/no/digital-meny-for-restauranter",
    },
  },
  {
    key: "qrMenu",
    labelKey: "front.landingSeo.links.qrMenu",
    path: {
      de: "/de/qr-code-speisekarte",
      en: "/en/qr-code-menu-for-restaurants",
      uk: "/uk/qr-menu-dlya-restoraniv",
      fr: "/fr/menu-qr-code-pour-restaurants",
      nl: "/nl/qr-code-menu-voor-restaurants",
      pl: "/pl/menu-z-kodem-qr-dla-restauracji",
      it: "/it/menu-con-codice-qr-per-ristoranti",
      da: "/da/qr-kode-menu-til-restauranter",
      es: "/es/menu-con-codigo-qr-para-restaurantes",
      no: "/no/qr-kode-meny-for-restauranter",
    },
  },
  {
    key: "multilingualMenu",
    labelKey: "front.landingSeo.links.multilingualMenu",
    path: {
      de: "/de/mehrsprachige-speisekarte",
      en: "/en/multilingual-menu-for-restaurants",
      uk: "/uk/bahatomovne-menu-dlya-restoraniv",
      fr: "/fr/menu-multilingue-pour-restaurants",
      nl: "/nl/meertalig-menu-voor-restaurants",
      pl: "/pl/wielojezyczne-menu-dla-restauracji",
      it: "/it/menu-multilingue-per-ristoranti",
      da: "/da/flersproget-menu-til-restauranter",
      es: "/es/menu-multilingue-para-restaurantes",
      no: "/no/flerspraklig-meny-for-restauranter",
    },
  },
  {
    key: "reservation",
    labelKey: "front.landingSeo.links.reservation",
    path: {
      de: "/de/online-tischreservierung",
      en: "/en/online-table-reservation-for-restaurants",
      uk: "/uk/onlain-broniuvannia-stolykiv-dlya-restoraniv",
      fr: "/fr/reservation-table-en-ligne-pour-restaurants",
      nl: "/nl/online-tafelreservering-voor-restaurants",
      pl: "/pl/rezerwacja-stolikow-online-dla-restauracji",
      it: "/it/prenotazione-tavoli-online-per-ristoranti",
      da: "/da/online-bordreservation-til-restauranter",
      es: "/es/reserva-de-mesas-online-para-restaurantes",
      no: "/no/online-bordreservasjon-for-restauranter",
    },
  },
  {
    key: "freeDigitalMenu",
    labelKey: "front.landingSeo.links.freeDigitalMenu",
    path: {
      de: "/de/digitale-speisekarte-kostenlos-testen",
      en: "/en/free-digital-menu-test",
      uk: "/uk/bezkoshtovne-tsyfrove-menu-test",
      fr: "/fr/tester-menu-numerique-gratuitement",
      nl: "/nl/gratis-digitaal-menu-testen",
      pl: "/pl/przetestuj-bezplatne-menu-cyfrowe",
      it: "/it/prova-gratis-menu-digitale",
      da: "/da/test-gratis-digital-menu",
      es: "/es/probar-menu-digital-gratis",
      no: "/no/test-gratis-digital-meny",
    },
  },
  {
    key: "changeMenuOnline",
    labelKey: "front.landingSeo.links.changeMenuOnline",
    path: {
      de: "/de/speisekarte-online-aendern",
      en: "/en/change-menu-online",
      uk: "/uk/zminyty-menu-online",
      fr: "/fr/modifier-menu-en-ligne",
      nl: "/nl/menu-online-wijzigen",
      pl: "/pl/zmieniaj-menu-online",
      it: "/it/modifica-menu-online",
      da: "/da/rediger-menu-online",
      es: "/es/cambiar-menu-online",
      no: "/no/endre-meny-online",
    },
  },
];

function getSeoLinkText(labels, lang) {
  return labels?.[lang] || labels?.en || labels?.de || "";
}


export default function LandingPage({ forcedLang = null }) {
  const navigate = useNavigate();
  const location = useLocation();

  const initialPhoneCountry = useMemo(() => detectCountryFromBrowser(), []);

  const [defaultCountry, setDefaultCountry] = useState(initialPhoneCountry);
  const [phoneCountry, setPhoneCountry] = useState(initialPhoneCountry);

  const defaultCountryRef = useRef(defaultCountry);
  const phoneCountryRef = useRef(phoneCountry);

  useEffect(() => {
    defaultCountryRef.current = defaultCountry;
  }, [defaultCountry]);

  useEffect(() => {
    phoneCountryRef.current = phoneCountry;
  }, [phoneCountry]);


  const routeLang = normalizeLandingLang(forcedLang);

const [lang, setLang] = useState(() => {
  if (AVAILABLE_LANGS.includes(forcedLang)) return routeLang;
  return normalizeLandingLang(safeReadLandingLang() || "de");
});

useLandingSeo(lang);

useEffect(() => {
  if (!AVAILABLE_LANGS.includes(forcedLang)) return;

  setLang(routeLang);

  try {
    window.localStorage.setItem(LANDING_LANG_STORAGE_KEY, routeLang);
  } catch {}
}, [forcedLang, routeLang]);

  const ta = useCallback((key, fallback) => tLang(key, lang, fallback), [lang]);

  const landingPath = LANDING_PATH_BY_LANG[lang] || "/de/";

const authPath = useCallback(
  (mode) => {
    const safeMode = mode === "login" ? "login" : "register";
    const basePath = LANDING_PATH_BY_LANG[lang] || "/de/";
    return `${basePath}${safeMode}`;
  },
  [lang]
);

const localizedPath = useCallback(
  (pathsOrDe = {}, enPath = "") => {
    // старий формат:
    // localizedPath("/de/page", "/en/page")
    if (typeof pathsOrDe === "string") {
      if (lang === "en" && enPath) return enPath;
      return pathsOrDe;
    }

    // новий формат:
    // localizedPath({ de: "...", en: "...", uk: "...", fr: "..." })
    if (pathsOrDe && typeof pathsOrDe === "object") {
      return pathsOrDe[lang] || pathsOrDe.de || "/";
    }

    return "/";
  },
  [lang]
);

const legalPath = useCallback(
  (slug) => {
    const basePath = LANDING_PATH_BY_LANG[lang] || "/de/";
    return `${basePath}${slug}`;
  },
  [lang]
);

  const handleLangChange = useCallback(
  (nextLang) => {

    const v = normalizeLandingLang(nextLang);

    setLang(v);

    try {
      window.localStorage.setItem(LANDING_LANG_STORAGE_KEY, v);
    } catch {}

    const currentStep = getAuthStepFromPath(location.pathname);
    const basePath = LANDING_PATH_BY_LANG[v] || "/de/";

    if (currentStep) {
      navigate(`${basePath}${currentStep}`);
      return;
    }

    navigate(basePath);
  },
  [navigate, location.pathname]
);

  const routeAuthStep = getAuthStepFromPath(location.pathname);

const [step, setStep] = useState(() => routeAuthStep);

const panelOpen = step !== null;

const [headerMenuOpen, setHeaderMenuOpen] = useState(false);

const closeHeaderMenu = useCallback(() => {
  setHeaderMenuOpen(false);
}, []);

const toggleHeaderMenu = useCallback(() => {
  setHeaderMenuOpen((prev) => !prev);
}, []);

useEffect(() => {
  if (!headerMenuOpen) return;

  const onKeyDown = (e) => {
    if (e.key === "Escape") {
      closeHeaderMenu();
    }
  };

  window.addEventListener("keydown", onKeyDown);
  return () => window.removeEventListener("keydown", onKeyDown);
}, [headerMenuOpen, closeHeaderMenu]);

  const [notice, setNotice] = useState(null);
  const [resetStage, setResetStage] = useState("request");
  const [resetCode, setResetCode] = useState("");
  const [resetNewPass, setResetNewPass] = useState("");

  const showInfo = useCallback((text) => setNotice({ text, kind: "info" }), []);
  const showSuccess = useCallback((text) => setNotice({ text, kind: "success" }), []);
  const showError = useCallback((text) => setNotice({ text, kind: "error" }), []);
  const clearNotice = useCallback(() => setNotice(null), []);

  const [formData, setFormData] = useState({ email: "", password: "" });
  const [fieldError, setFieldError] = useState({ phone: false, password: false });

  useEffect(() => {
  const nextStep = getAuthStepFromPath(location.pathname);

  if (nextStep) {
    clearNotice();
    setFieldError({ phone: false, password: false });
    setStep(nextStep);
  }
}, [location.pathname, clearNotice]);
  
  
  
  const phoneValueRef = useRef("");

  useEffect(() => {
    phoneValueRef.current = formData.email;
  }, [formData.email]);

  useEffect(() => {
    let alive = true;

    detectCountryFromIp().then((ipCountry) => {
      if (!alive || !ipCountry) return;

      const currentDefault = defaultCountryRef.current;
      const currentPhone = phoneCountryRef.current;

      // Не перетираємо ручний вибір і вже введений номер.
      if (phoneValueRef.current) return;
      if (currentPhone !== currentDefault) return;

      setDefaultCountry(ipCountry);
      setPhoneCountry(ipCountry);
    });

    return () => {
      alive = false;
    };
  }, []);

  const clearFieldError = useCallback((key) => {
    setFieldError((prev) => (prev[key] ? { ...prev, [key]: false } : prev));
  }, []);

  const handleChange = useCallback(
    (e) => {
      const { name, value } = e.target;
      setFormData((prev) => ({ ...prev, [name]: value }));

      if (notice?.kind === "error") setNotice(null);
      if (name === "password") clearFieldError("password");
    },
    [notice, clearFieldError]
  );

  const activePhoneCountry = phoneCountry || defaultCountry || "DE";



  const phoneInputComponent = useMemo(() => {
  return React.forwardRef(function PhoneInputComponent(props, ref) {
    return (
      <PhoneField
        {...props}
        ref={ref}
        inputRef={props.inputRef}
        country={activePhoneCountry}
      />
    );
  });
}, [activePhoneCountry]);

  const handlePhoneChange = useCallback(
    (value) => {
      setFormData((prev) => ({
        ...prev,
        email: value || "",
      }));

      if (notice?.kind === "error") setNotice(null);
      clearFieldError("phone");
    },
    [notice, clearFieldError]
  );
  
  const handlePhoneCountryChange = useCallback(
    (nextCountry) => {
      const country = normalizeCountryCode(nextCountry);
      if (!country) return;

      setPhoneCountry(country);

      clearFieldError("phone");
      if (notice?.kind === "error") setNotice(null);
    },
    [notice, clearFieldError]
  );

  const [newClientId, setNewClientId] = useState(null);

  const openReset = useCallback(() => {
    clearNotice();
    setResetStage("request");
    setResetCode("");
    setResetNewPass("");
    setStep("reset");
  }, [clearNotice]);

  async function requestReset() {
    try {
      const normalizedPhone = normalizePhoneForCountry(
        formData.email,
        activePhoneCountry
      );

      if (!normalizedPhone || getLocalDigitsLength(normalizedPhone, activePhoneCountry) === 0) {
        showError(lang === "en" ? "Enter phone number." : "Bitte Telefonnummer eingeben.");
        return;
      }

      showInfo(lang === "en" ? "Sending code..." : "Code wird gesendet...");

      const res = await fetch(ENDPOINTS.resetRequest(), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: normalizedPhone }),
      });

      const data = await safeJson(res);

      const msg = data?.devCode
        ? `DEV code: ${data.devCode}`
        : lang === "en"
        ? "If this phone exists, you’ll receive a code."
        : "Wenn die Nummer existiert, bekommst du einen Code.";

      setFormData((prev) => ({ ...prev, email: normalizedPhone }));
      showSuccess(msg);
      setResetStage("confirm");
    } catch {
      showError(lang === "en" ? "Connection error." : "Verbindungsfehler.");
    }
  }

  async function confirmReset() {
    const code = String(resetCode || "").trim();
    const newPass = String(resetNewPass || "");
    const normalizedPhone = normalizePhoneForCountry(formData.email, activePhoneCountry);

    if (!normalizedPhone || getLocalDigitsLength(normalizedPhone, activePhoneCountry) === 0) {
      showError(lang === "en" ? "Enter phone number." : "Bitte Telefonnummer eingeben.");
      return;
    }

    if (!code) {
      showError(lang === "en" ? "Enter the code." : "Bitte Code eingeben.");
      return;
    }

    if (!newPass || newPass.length < 6) {
      showError(
        lang === "en" ? "New password is too short." : "Neues Passwort ist zu kurz."
      );
      return;
    }

    try {
      showInfo(lang === "en" ? "Updating password..." : "Passwort wird geändert...");

      const res = await fetch(ENDPOINTS.resetConfirm(), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: normalizedPhone,
          code,
          newPassword: newPass,
        }),
      });

      const data = await safeJson(res);

      if (!res.ok || data?.ok === false) {
        showError(
          lang === "en"
            ? "Invalid code or expired."
            : "Code ist ungültig oder abgelaufen."
        );
        return;
      }

      showSuccess(
        lang === "en"
          ? "Password updated. Please log in."
          : "Passwort geändert. Bitte einloggen."
      );

      setFormData((prev) => ({
        ...prev,
        email: normalizedPhone,
        password: newPass,
      }));

      setStep("login");
      setResetStage("request");
      setResetCode("");
      setResetNewPass("");
    } catch {
      showError(lang === "en" ? "Connection error." : "Verbindungsfehler.");
    }
  }

  const [plan, setPlan] = useState("free");

  const [subscriptionPricing, setSubscriptionPricing] =
    useState(DEFAULT_SUBSCRIPTION_PRICING);

  const [subdomain, setSubdomain] = useState("");
  const [subError, setSubError] = useState("");
  const [acceptLegal, setAcceptLegal] = useState(false);

  const closePanel = useCallback(() => {
    setStep(null);
    clearNotice();
    setSubError("");
    setAcceptLegal(false);
    setResetStage("request");
    setResetCode("");
    setResetNewPass("");
    setNewClientId(null);
    setPlan("free");
    setSubscriptionPricing(
      DEFAULT_SUBSCRIPTION_PRICING
    );
    setSubdomain("");
    setFormData({ email: "", password: "" });
    setFieldError({ phone: false, password: false });
    navigate(landingPath, { replace: true });
  }, [clearNotice, landingPath, navigate]);

  useEffect(() => {
    if (!panelOpen) return;

    const onKeyDown = (e) => {
      if (e.key === "Escape") closePanel();
    };

    document.addEventListener("keydown", onKeyDown);

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [panelOpen, closePanel]);

  const firstRef = useRef(null);

  useEffect(() => {
    if (!panelOpen) return;
    setTimeout(() => firstRef.current?.focus?.(), 0);
  }, [panelOpen, step]);

  const validateSubdomain = useCallback((value) => {
    const v = value.trim().toLowerCase();
    if (!v) return "Bitte geben Sie einen Subdomain-Namen ein";
    if (!/^[a-z0-9-]{3,30}$/.test(v)) return "Nur Buchstaben, Zahlen und '-', 3–30 Zeichen";
    if (/^-|-$/.test(v)) return "Subdomain darf nicht mit '-' beginnen oder enden";
    if (["www", "admin", "api", "test"].includes(v)) return "Dieser Subdomain-Name ist reserviert";
    return "";
  }, []);

  const openLogin = useCallback(() => {
  clearNotice();
  setFieldError({ phone: false, password: false });
  setStep("login");
  navigate(authPath("login"));
}, [authPath, clearNotice, navigate]);

  const openRegister = useCallback(() => {
  clearNotice();
  setSubError("");
  setSubdomain("");
  setNewClientId(null);
  setPlan("free");
  setSubscriptionPricing(
    DEFAULT_SUBSCRIPTION_PRICING
  );
  setAcceptLegal(false);
  setFormData({ email: "", password: "" });
  setFieldError({ phone: false, password: false });
  setStep("register");
  navigate(authPath("register"));
}, [authPath, clearNotice, navigate]);

  const inFlightRef = useRef(false);

  const handleAuthSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      if (inFlightRef.current) return;
      if (step !== "login" && step !== "register") return;

      const L = normalizeLandingLang(lang);

      const msgFromCode = (code) => {
        switch (code) {
          case "REQUIRED":
            return L === "en"
              ? "Phone number and password are required."
              : "Telefonnummer und Passwort sind erforderlich.";
          case "INVALID_CREDENTIALS":
            return L === "en"
              ? "Wrong phone or password."
              : "Falsche Telefonnummer oder Passwort.";
          case "SIGNUP_INCOMPLETE":
            return L === "en"
              ? "Account is not activated yet. Please complete signup."
              : "Konto ist noch nicht aktiviert. Bitte Registrierung abschließen.";
          case "EMAIL_EXISTS_ACTIVE":
            return L === "en"
              ? "A user with this phone already exists. Please log in."
              : "Ein Benutzer mit dieser Telefonnummer existiert bereits. Bitte anmelden.";
          case "MENU_INVALID":
            return L === "en"
              ? "menu.json is invalid or missing."
              : "menu.json ist ungültig oder fehlt.";
          case "SERVER_ERROR":
          default:
            return L === "en"
              ? "Server error. Please try again."
              : "Serverfehler. Bitte erneut versuchen.";
        }
      };

      inFlightRef.current = true;

      try {
        setFieldError({ phone: false, password: false });

        const country = phoneCountry || defaultCountry || "DE";
        const normalizedPhone = normalizePhoneForCountry(formData.email, country);
        const localLen = getLocalDigitsLength(normalizedPhone, country);
        const pass = String(formData.password || "");

        if (step === "register" && !acceptLegal) {
          showError(
            ta(
              "landing.legal.required",
              L === "en"
                ? "Please accept the Terms and Privacy Policy."
                : "Bitte stimme den Terms und der Privacy Policy zu."
            )
          );
          return;
        }

        let phoneErr = "";
        let passErr = "";

        if (!normalizedPhone || localLen === 0) {
          phoneErr =
            L === "en"
              ? "Phone number is required."
              : "Telefonnummer ist erforderlich.";
        } else if (!isPossiblePhoneNumber(normalizedPhone)) {
          phoneErr =
            L === "en"
              ? "Phone number length looks wrong."
              : "Telefonnummer hat eine falsche Länge.";
        } else if (!isValidPhoneNumber(normalizedPhone)) {
          phoneErr =
            L === "en"
              ? "Invalid phone number."
              : "Ungültige Telefonnummer.";
        }

        if (!pass) {
          passErr = L === "en" ? "Password is required." : "Passwort ist erforderlich.";
        } else if (pass.length < 6) {
          passErr = L === "en" ? "Password is too short." : "Passwort ist zu kurz.";
        }

        if (phoneErr || passErr) {
          setFieldError({ phone: !!phoneErr, password: !!passErr });
          showError(phoneErr || passErr);
          return;
        }

        showInfo(
          step === "login"
            ? ta("landing.loggingIn", L === "en" ? "Logging in..." : "Login...")
            : ta(
                "landing.registering",
                L === "en" ? "Registering..." : "Registrierung..."
              )
        );

        const endpoint = step === "login" ? ENDPOINTS.login() : ENDPOINTS.register();

        const payload =
          step === "login"
            ? { ...formData, email: normalizedPhone, lang: L }
            : { ...formData, email: normalizedPhone, acceptLegal, lang: L };

        const res = await fetch(endpoint, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });

        const data = await safeJson(res);

        if (!res.ok || data?.ok === false) {
          const fallbackText =
            data?.message ||
            msgFromCode(data?.code) ||
            (L === "en" ? "Error. Please try again." : "Fehler. Bitte erneut versuchen.");

          showError(ta(data?.tKey || "server.common.unknown", fallbackText));
          return;
        }

        setFormData((prev) => ({ ...prev, email: normalizedPhone }));

        if (step === "login") {
          showSuccess(
            ta(
              data?.tKey || "server.login.success",
              data?.message || (L === "en" ? "Success." : "Erfolgreich.")
            )
          );

          const rawUrl = typeof data?.url === "string" ? data.url : "";
          if (rawUrl) {
            window.location.href = rawUrl;
            return;
          }

          if (data?.id) navigate(`/admin/${data.id}`);
          else navigate("/");
          return;
        }

        // REGISTER success
        await notifyTelegramAboutRegister(normalizedPhone, L);

        clearNotice();

        const clientId = data?.id ? String(data.id) : null;
        setNewClientId(clientId);

        const nextPlan = data?.plan || "free";
        setPlan(nextPlan);

        const nextSubscriptionPricing =
          normalizeSubscriptionPricing(data?.pricing);

        setSubscriptionPricing(
          nextSubscriptionPricing
        );

        if (data?.signupStep === "subdomain") {
          setStep("subdomain");
        } else {
          setStep("plan");
        }
      } catch (err) {
        console.error("handleAuthSubmit error:", err);
        showError(
          ta(
            "server.common.network",
            lang === "en" ? "Connection error." : "Verbindungsfehler."
          )
        );
      } finally {
        inFlightRef.current = false;
      }
    },
    [
      step,
      formData,
      acceptLegal,
      lang,
      navigate,
      ta,
      showInfo,
      showError,
      showSuccess,
      clearNotice,
      phoneCountry,
      defaultCountry,
    ]
  );

  const backFromPlan = useCallback(() => {
    setStep("register");
    clearNotice();
  }, [clearNotice]);

  const nextFromPlan = useCallback(async () => {
  const clientId = newClientId;

  if (!clientId) {
    showError(lang === "en" ? "Error: clientId is missing" : "Fehler: clientId fehlt");
    return;
  }

  clearNotice();

  if (plan === "free") {
    try {
      showInfo(lang === "en" ? "Finishing signup..." : "Registrierung abschließen...");

      const completeRes = await fetch(ENDPOINTS.signupComplete(), {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ clientId }),
      });

      const completeData = await safeJson(completeRes);

      if (!completeRes.ok || completeData?.ok === false) {
        showError(
          completeData?.message ||
            (lang === "en"
              ? "Signup could not be completed."
              : "Registrierung konnte nicht abgeschlossen werden.")
        );
        return;
      }

      // ВАЖЛИВО:
      // Після активації акаунта одразу логінимо нового користувача,
      // щоб старий bp_auth перезаписався на новий tenantId.
      const loginRes = await fetch(ENDPOINTS.login(), {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password,
          lang: normalizeLandingLang(lang),
        }),
      });

      const loginData = await safeJson(loginRes);

      if (!loginRes.ok || loginData?.ok === false) {
        showError(
          lang === "en"
            ? "Account created. Please log in again."
            : "Konto erstellt. Bitte melde dich erneut an."
        );
        setStep("login");
        return;
      }

      navigate(`/admin/${clientId}`, { replace: true });
      return;
    } catch (e) {
      console.warn("signup-complete/login failed:", e);
      showError(
        lang === "en"
          ? "Connection error."
          : "Verbindungsfehler."
      );
      return;
    }
  }

  setStep("subdomain");
}, [
  newClientId,
  plan,
  navigate,
  clearNotice,
  showError,
  showInfo,
  lang,
  formData.email,
  formData.password,
]);

  const handleSubdomainSubmit = useCallback(
    async (e) => {
      e.preventDefault();

      const clientId = newClientId;
      if (!clientId) {
        showError(lang === "en" ? "Error: clientId is missing" : "Fehler: clientId fehlt");
        return;
      }

      const err = validateSubdomain(subdomain);
      if (err) {
        setSubError(err);
        return;
      }

      setSubError("");
      showInfo(lang === "en" ? "Registering subdomain..." : "Subdomain wird registriert...");

      try {
        const res = await fetch(ENDPOINTS.registerSubdomain(), {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            clientId,
            subdomain: subdomain.trim().toLowerCase(),
            plan,
          }),
        });

        const data = await safeJson(res);

        if (!res.ok || data?.ok === false) {
          const fallback =
            data?.message ||
            (lang === "en"
              ? "Subdomain registration failed."
              : "Fehler bei der Subdomain-Registrierung");

          showError(ta(data?.tKey || "server.subdomain.error", fallback));
          return;
        }

        showSuccess(lang === "en" ? "Success! Redirecting..." : "Erfolgreich! Weiterleitung...");

        const rawUrl = typeof data.url === "string" ? data.url : "";
        if (rawUrl) {
          let origin = "";
          try {
            origin = new URL(rawUrl).origin;
          } catch {
            const m = rawUrl.match(/^https?:\/\/[^/]+/i);
            if (m) origin = m[0];
          }

          if (origin) {
            window.location.href = `${origin}/admin/${clientId}`;
            return;
          }
        }

        navigate(`/admin/${clientId}`);
      } catch (err2) {
        console.error(err2);
        showError(
          lang === "en"
            ? "Failed to connect to server"
            : "Fehler beim Verbinden mit dem Server"
        );
      }
    },
    [navigate, newClientId, subdomain, validateSubdomain, plan, ta, lang, showInfo, showError, showSuccess]
  );

  const panelTitle = useMemo(() => {
    if (step === "login") return ta("landing.loginTitle", "Login");
    if (step === "register") return ta("landing.registerTitle", "Registrierung");
    if (step === "reset") return ta("landing.resetTitle", "Passwort zurücksetzen");
    if (step === "plan") return ta("landing.tariff.selection", "Tarif wählen");
    if (step === "subdomain") return ta("landing.tariff.subdomain");
    return "";
  }, [step, ta]);

  return (
    <div className="landing-shell">
      <div className="services">
        <div className="services__hero">
          <div className="services__image">
            <div className="header--seo__wrapper">
              <div className="header--seo__logo-lang">
                <Link
                  to={LANDING_PATH_BY_LANG[lang] || "/de/"}
                  className="logo-wrapper-land"
                  aria-label="Home"
                  onClick={closeHeaderMenu}
                />

                <LangSelect
                  value={lang}
                  onChange={(nextLang) => {
                    handleLangChange(nextLang);
                    closeHeaderMenu();
                  }}
                  availableLangs={AVAILABLE_LANGS}
                />
              </div>
              

              <div className="services__header--block">
                

                <div
                  className={`header--seo__menuButton${
                    headerMenuOpen ? " header--seo__menuButton--open" : ""
                  }`}
                  aria-label={ta("landing.header.openMenu", "Menü öffnen")}
                  aria-pressed={headerMenuOpen}
                  aria-expanded={headerMenuOpen}
                  aria-controls="landingHeaderNavList"
                  onClick={toggleHeaderMenu}
                >
                  <span />
                  <span />
                  <span />
                </div>

                <ul
                  id="landingHeaderNavList"
                  className={`header--seo__navList${
                    headerMenuOpen ? " header--seo__navList--open" : ""
                  }`}
                >
                  <li className="header--seo__navItem">
                    {(() => {
                      const item = LANDING_FOOTER_MAIN_LINKS.find(
                        (link) => link.key === "contact"
                      );

                      return (
                        <Link
                          className="text__landig-content"
                          to={legalPath(item.slug)}
                          onClick={closeHeaderMenu}
                        >
                          {getFooterLinkText(item.label, lang)}
                        </Link>
                      );
                    })()}
                  </li>

                  

                  <li className="header--seo__navItem header--seo__navItem--socials">
                    <a
                      className="header--seo__socialLink"
                      href="https://wa.me/380967750115"
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="WhatsApp"
                      onClick={closeHeaderMenu}
                    >
                      <img
                        className="header--seo__socialIcon"
                        src={waIcon}
                        alt=""
                        aria-hidden="true"
                      />
                    </a>

                    <a
                      className="header--seo__socialLink"
                      href="https://t.me/Ruzhytskyy"
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Telegram"
                      onClick={closeHeaderMenu}
                    >
                      <img
                        className="header--seo__socialIcon"
                        src={tgIcon}
                        alt=""
                        aria-hidden="true"
                      />
                    </a>

                    <a
                      className="header--seo__socialLink"
                      href="https://www.instagram.com/_digi.space?igsh=NHhvOHIycHZweWUw&utm_source=qr"
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Instagram"
                      onClick={closeHeaderMenu}
                    >
                      <img
                        className="header--seo__socialIcon"
                        src={instIcon}
                        alt=""
                        aria-hidden="true"
                      />
                    </a>
                  </li>

                  <li className="header--seo__navItem">
                    {(() => {
                      const item = LANDING_FOOTER_MAIN_LINKS.find(
                        (link) => link.key === "video"
                      );

                      return (
                        <Link
                          to={item.getPath(lang)}
                          className="qrArticle__btn"
                          onClick={closeHeaderMenu}
                        >
                          {getFooterLinkText(item.label, lang)}
                        </Link>
                      );
                    })()}
                  </li>

                  <li className="header--seo__navItem">
                    <div
                      className="services__button-login"
                      onClick={() => {
                        closeHeaderMenu();
                        openLogin();
                      }}
                      aria-label="Login"
                    >
                      {ta("landing.title.login")}
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          <div className="services__wrapper">
            <div className="frost" aria-hidden="true" />

            

            <h1 className="title__landing">{ta("landing.title")}</h1>
            <p className="text__landing">{ta("landing.text")}</p>

            <div className="services__button-container">
              <button
                className="services__button-register services__button-register--top"
                type="button"
                onClick={openRegister}
              >
                {ta("landing.try.now")}
              </button>
            </div>
            
          </div>
        </div>
        </div>

        <div
          className={`landing-backdrop ${panelOpen ? "landing-backdrop--open" : ""}`}
          onClick={closePanel}
          aria-hidden={!panelOpen}
        />

        <aside
          className={`landing-panel ${panelOpen ? "landing-panel--open" : ""}`}
          aria-hidden={!panelOpen}
          role="dialog"
          aria-modal="true"
        >
          <div className="landing-panel__wrapper">
            <div className="landing-panel__head">
              <div className="logo-wrapper-admin"></div>

              <button className="services__clouse" type="button" onClick={closePanel}>
                ✕
              </button>
            </div>

            <div className="admin__panel-body admin__panel-body--mono">
              {(step === "login" || step === "register") && (
                <form className="services__form services__form--another" onSubmit={handleAuthSubmit} noValidate>
                  <div className="services__title services__title--walen">{panelTitle}</div>

                  <div className="form-input-fields-block">
                    <div className="form-input-fields">
                      <PhoneInput
                        key={`auth-phone-${defaultCountry}`}
                        className={`phoneField ${fieldError.phone ? "input--error" : ""}`}
                        international
                        defaultCountry={defaultCountry}
                        onCountryChange={handlePhoneCountryChange}
                        value={formData.email}
                        onChange={handlePhoneChange}
                        inputComponent={phoneInputComponent}
                        inputRef={firstRef}
                        countryCallingCodeEditable={false}
                      />
                    </div>

                    <div className="form-input-fields form-input-fields--with-addon">
                      <span className="input-addon input-addon--password" aria-hidden="true" />

                      <input
                        id="auth-password"
                        placeholder=" "
                        type="password"
                        name="password"
                        className={`input input--with-addon ${fieldError.password ? "input--error" : ""}`}
                        value={formData.password}
                        onChange={handleChange}
                        autoComplete={step === "login" ? "current-password" : "new-password"}
                      />
                      <label htmlFor="auth-password">{ta("landing.password")}</label>
                    </div>

                    {step === "login" && (
                      <div className="button__block button__block--center">
                        {/*<div className="linkLike" onClick={openReset}>
                          {ta("admin.forgot.password")}
                        </div>*/}
                        <div className="linkLike">
                          {ta("admin.forgot.password")}
                        </div>
                      </div>
                    )}
                  </div>

                  {step === "register" && (
                    <div className="services__legal">
                      <label className="services__legal-label">
                        <input
                          className="services__legal-checkbox"
                          type="checkbox"
                          checked={acceptLegal}
                          onChange={(e) => {
                            setAcceptLegal(e.target.checked);
                            if (notice?.kind === "error") setNotice(null);
                          }}
                        />
                        <span className="services__legal-text">
                          {ta("landing.legal.text.start")}
                          <Link
                            className="services__legal-link"
                            to={legalPath("terms")}
                            target="_blank"
                            rel="noreferrer"
                          >
                            {ta("footer.legal.terms")}
                          </Link>
                          {ta("landing.legal.text.mid")}
                          <Link
                            className="services__legal-link"
                            to={legalPath("privacy")}
                            target="_blank"
                            rel="noreferrer"
                          >
                            {ta("footer.legal.privacy")}
                          </Link>
                          {ta("footer.legal.privacy.and")}
                        </span>
                      </label>
                    </div>
                  )}

                  <div className="landing-panel__message-wrapper">
                    {notice?.text && (
                      <p className={`landing-panel__message landing-panel__message--${notice.kind || "info"}`}>
                        {notice.text}
                      </p>
                    )}
                  </div>

                  <div className="button__block">
                    <button className="services__button-prev" type="button" onClick={closePanel}>
                      {ta("admin.nav.back")}
                    </button>

                    <button className="services__button-mix" type="submit">
                      {step === "login" ? "Betreten" : ta("admin.nav.next", "Weiter")}
                    </button>
                  </div>
                </form>
              )}

              {step === "reset" && (
                <form
                  className="services__form services__form--another"
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (resetStage === "request") void requestReset();
                    else void confirmReset();
                  }}
                  noValidate
                >
                  <div className="services__title services__title--walen">{panelTitle}</div>

                  <div className="form-input-fields-block">
                    <div className="form-input-fields">
                      <PhoneInput
                        key={`reset-phone-${defaultCountry}`}
                        className="phoneField"
                        international
                        defaultCountry={defaultCountry}
                        onCountryChange={handlePhoneCountryChange}
                        value={formData.email}
                        onChange={handlePhoneChange}
                        inputComponent={phoneInputComponent}
                        inputRef={firstRef}
                        countryCallingCodeEditable={false}
                      />
                    </div>

                    {resetStage === "confirm" && (
                      <>
                        <div className="form-input-fields">
                          <input
                            className="input"
                            placeholder=" "
                            value={resetCode}
                            onChange={(e) => setResetCode(e.target.value)}
                            inputMode="numeric"
                            autoComplete="one-time-code"
                          />
                          <label>Code</label>
                        </div>

                        <div className="form-input-fields form-input-fields--with-addon">
                          <span className="input-addon input-addon--password" aria-hidden="true" />
                          <input
                            className="input input--with-addon"
                            placeholder=" "
                            type="password"
                            value={resetNewPass}
                            onChange={(e) => setResetNewPass(e.target.value)}
                            autoComplete="new-password"
                          />
                          <label>{ta("landing.password", "Password")}</label>
                        </div>
                      </>
                    )}
                  </div>

                  <div className="landing-panel__message-wrapper">
                    {notice?.text && (
                      <p className={`landing-panel__message landing-panel__message--${notice.kind || "info"}`}>
                        {notice.text}
                      </p>
                    )}
                  </div>

                  <div className="button__block">
                    <button
                      className="services__button-prev"
                      type="button"
                      onClick={() => {
                        clearNotice();
                        setResetStage("request");
                        setResetCode("");
                        setResetNewPass("");
                        setStep("login");
                      }}
                    >
                      {ta("admin.nav.back", "Back")}
                    </button>

                    <button className="services__button-mix" type="submit">
                      {resetStage === "request" ? "Code senden" : "Passwort ändern"}
                    </button>
                  </div>

                  {resetStage === "confirm" && (
                    <button
                      type="button"
                      className="linkLike"
                      onClick={() => {
                        clearNotice();
                        setResetStage("request");
                        setResetCode("");
                        setResetNewPass("");
                      }}
                    >
                      Code erneut senden
                    </button>
                  )}
                </form>
              )}

              {step === "plan" && (
                <form
                  className="services__form"
                  onSubmit={(e) => {
                    e.preventDefault();
                    void nextFromPlan();
                  }}
                  noValidate
                >
                  <div className="services__form-wrapper">
                    <Price
                      value={plan}
                      onChange={setPlan}
                      ta={ta}
                      pricing={subscriptionPricing}
                    />

                    <div className="button__block">
                      <button className="services__button-prev" type="button" onClick={backFromPlan}>
                        {ta("admin.nav.back")}
                      </button>

                      <button className="services__button-mix" type="submit">
                        {ta("admin.nav.next")}
                      </button>
                    </div>
                  </div>
                </form>
              )}

              {step === "subdomain" && (
                <SubdomainForm
                  panelTitle={panelTitle}
                  plan={plan}
                  ta={ta}
                  firstRef={firstRef}
                  subdomain={subdomain}
                  setSubdomain={setSubdomain}
                  subError={subError}
                  notice={notice}
                  clearNotice={clearNotice}
                  onBack={() => {
                    setStep("plan");
                    clearNotice();
                    setSubdomain("");
                    setSubError("");
                  }}
                  onSubmit={handleSubdomainSubmit}
                />
              )}
            </div>
          </div>
        </aside>

        <div className="countryBannerBlock">
          <div className="title__to-wrapper">
            <div className="title title__to">
              {ta("front.landingSeo.links.multilingualMenu")}
            </div>
          </div>

          <CountryBanner
            reverse
            ariaLabel={ta("landing.countryBanner.aria", "Countries and languages")}
          />
        </div>

        <div className="services__contant-wrapper">
          <div className="title__to-wrapper">
            <div className="title title__to">{ta("landing.title.to")}</div>
          </div>
          <div className="title__to-wrapper">
            <div className="title title__description title__description--answer">
              {ta("landing.title.answer")}
            </div>
          </div>
          <div className="services__contant--for-center">
            <div className="services__contant">
              <div className="services__contant-block">
                <p className="text text__landig-content-title text__landig-content-title--first">
                  {ta("landing.content.title.first")}
                </p>
                <p className="text text__gray">{ta("landing.content.first")}</p>
              </div>
              <div className="services__contant-block">
                <p className="text text__landig-content-title text__landig-content-title--second">
                  {ta("landing.content.title.second")}
                </p>
                <p className="text text__gray">{ta("landing.content.second")}</p>
              </div>
              <div className="services__contant-block">
                <p className="text text__landig-content-title text__landig-content-title--third">
                  {ta("landing.content.title.third")}
                </p>
                <p className="text text__gray">{ta("landing.content.third")}</p>
              </div>
              <div className="services__contant-block">
                <p className="text text__landig-content-title text__landig-content-title--four">
                  {ta("landing.content.title.four")}
                </p>
                <p className="text text__gray">{ta("landing.content.four")}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="lending-contact__block">
          <SeoContactSection
          lang={lang}
          apiOrigin={API_ORIGIN}
        />
        </div>
        
      </div>
      <footer className="landingFooter" role="contentinfo">
          <div className="landingFooter__arc" aria-hidden="true">
            <svg viewBox="0 0 1440 120" preserveAspectRatio="none">
              <path d="M0,120 C480,20 960,20 1440,120 L1440,120 L0,120 Z" fill="currentColor" />
            </svg>
          </div>

          <div className="landingFooter__inner">
            <div className="landingFooter__container--wrapper">
              <div className="landingFooter__container">
              <div className="text__container">
                <div className="text__container--logo"></div>
              </div>
              
              <div className="text text__white text__white--logo">best-place.club</div>
              <p className="text__landing">{ta("footer.about")}</p>

              <div className="services__button-container">
                <button
                  className="services__button-register services__button-register--top"
                  type="button"
                  onClick={openRegister}
                >
                  {ta("landing.try.now")}
                </button>
              </div>

              <div className="landingFooter__support">
                <div className="text text__white">
                  {ta("footer.support.title", "Support")}
                </div>

                <div className="landingFooter__block">
                  <p>
                    <a
                      className="text__landing landingFooter__link"
                      href="mailto:ivanruzhytskyy@gmail.com"
                    >
                      ivanruzhytskyy@gmail.com
                    </a>
                  </p>
                  <p>
                    <a
                      className="text__landing landingFooter__link"
                      href="tel:+380967750115"
                    >
                      +38 (096) 77-50-115
                    </a>
                  </p>
                </div>
              </div>
            </div>

            <div className="landingFooter__col">
              <div className="text text__white">
                {ta("footer.blog.title", "Blog")}
              </div>

              <nav className="landingFooter__links" aria-label="Blog links">
                {LANDING_SEO_LINKS.map((item) => (
                  <Link
                    key={item.key}
                    className="landingFooter__link-legal"
                    to={localizedPath(item.path)}
                  >
                    {tLang(item.labelKey, lang, item.key)}
                  </Link>
                ))}
              </nav>
            </div>
            </div>

            <div className="landingFooter__links-block--wrapper">
              <div className="landingFooter__links-block">
                <div className="landingFooter__col">
                  <div className="text text__white text__white--one">
                    {ta("footer.links.title", "Links")}
                  </div>

                  <nav className="landingFooter__links" aria-label="Footer links">
                    {LANDING_FOOTER_MAIN_LINKS.map((item) => (
                      <Link
                        key={item.key}
                        className="landingFooter__link-legal"
                        to={item.getPath ? item.getPath(lang) : legalPath(item.slug)}
                      >
                        {getFooterLinkText(item.label, lang)}
                      </Link>
                    ))}
                  </nav>
                </div>

                <div className="landingFooter__col">
                  <div className="text text__white">
                    {ta("footer.legal.title", "Legal")}
                  </div>

                  <nav className="landingFooter__links" aria-label="Legal links">
                    {LANDING_FOOTER_LEGAL_LINKS.map((item) => (
                      <Link
                        key={item.key}
                        className="landingFooter__link-legal"
                        to={legalPath(item.slug)}
                      >
                        {getFooterLinkText(item.label, lang)}
                      </Link>
                    ))}
                  </nav>
                </div>
              </div>
            </div>

            <div className="landingFooter__bottom">
              <div className="text__w">© {new Date().getFullYear()} best-place.club</div>
            </div>
          </div>
        </footer>
        <CallbackWidget
          apiOrigin={API_ORIGIN}
          hidden={false}
          variant="glass"
          lang={lang}
        />
      
    </div>
  );
}